import React from 'react';

import { cn } from '../../base/utils';

import { SkeletonScope, SkeletonItem, type SkeletonGroupAnimation } from '../../skeleton/motion';

import { Skeleton } from '../Skeleton';

export interface SkeletonTableProps extends React.HTMLAttributes<HTMLDivElement> {
  animation?: SkeletonGroupAnimation;
  /**
   * 列数
   * @default 4
   */
  columns?: number;
  /**
   * 行数
   * @default 5
   */
  rows?: number;
  /**
   * 是否显示表头
   * @default true
   */
  showHeader?: boolean;
  /**
   * 是否显示复选框列
   * @default false
   */
  showCheckbox?: boolean;
  /**
   * 列宽度配置（百分比数组）
   */
  columnWidths?: number[];
}

/**
 * 表格骨架屏组件
 */
export function SkeletonTable({
  className,
  animation,
  columns = 4,
  rows = 5,
  showHeader = true,
  showCheckbox = false,
  columnWidths,
  ...props
}: SkeletonTableProps) {
  const actualColumns = showCheckbox ? columns + 1 : columns;
  const defaultWidth = 100 / actualColumns;

  const getColumnWidth = (index: number) => {
    if (showCheckbox && index === 0) return '40px';
    const colIndex = showCheckbox ? index - 1 : index;
    if (columnWidths && columnWidths[colIndex]) {
      return `${columnWidths[colIndex]}%`;
    }
    return `${defaultWidth}%`;
  };

  return (
    <SkeletonScope animation={animation}>
      <div className={cn('w-full overflow-hidden rounded-md border', className)} {...props}>
        {/* 表头 */}
        {showHeader && (
          <SkeletonItem index={0}>
            <div className="flex border-b bg-muted/50 px-4 py-3">
              {Array.from({ length: actualColumns }).map((_, index) => (
                <div key={index} className="px-2" style={{ width: getColumnWidth(index) }}>
                  {showCheckbox && index === 0 ? (
                    <Skeleton className="size-4 rounded" />
                  ) : (
                    <Skeleton className="h-4 w-3/4 rounded" />
                  )}
                </div>
              ))}
            </div>
          </SkeletonItem>
        )}

        {/* 表体 */}
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <SkeletonItem key={rowIndex} index={rowIndex + (showHeader ? 1 : 0)}>
            <div className={cn('flex px-4 py-3', rowIndex !== rows - 1 && 'border-b')}>
              {Array.from({ length: actualColumns }).map((_, colIndex) => (
                <div key={colIndex} className="px-2" style={{ width: getColumnWidth(colIndex) }}>
                  {showCheckbox && colIndex === 0 ? (
                    <Skeleton className="size-4 rounded" />
                  ) : (
                    <Skeleton
                      className="h-4 rounded"
                      style={{ width: `${60 + Math.random() * 30}%` }}
                    />
                  )}
                </div>
              ))}
            </div>
          </SkeletonItem>
        ))}
      </div>
    </SkeletonScope>
  );
}
